import React from 'react';
import { createPortal } from 'react-dom';
import { NmProgress } from './NmProgress';
import { useTranslation } from 'react-i18next';
import { NmLoader } from './NmLoader';
export { NmLoader } from './NmLoader';

interface NmLoadingOverlayProps {
  message?: string;
  detail?: string;
  /** 0–100 shows a determinate progress bar alongside the brand loader. */
  progress?: number;
  /** `page` = full panel overlay; `inline` = calendar grid overlay. */
  variant?: 'page' | 'inline';
  style?: React.CSSProperties;
}

/** Theme-aware loading surface with the shared brand motion and optional progress. */
export function NmLoadingOverlay({
  message,
  detail,
  progress,
  variant = 'page',
  style,
}: NmLoadingOverlayProps) {
  const { t } = useTranslation();
  const showProgress = typeof progress === 'number';
  const clampedProgress = showProgress ? Math.max(0, Math.min(100, progress)) : 0;

  const overlay = (
    <div
      className={`nm-loading-overlay nm-loading-overlay--brand nm-loading-overlay--${variant} animate-fadein`}
      style={style}
      role="alert"
      aria-busy="true"
      aria-label={message ?? t('common.loading')}
    >
      <div className="nm-loading-overlay__card">
        <NmLoader size={variant === 'page' ? 'lg' : 'md'} decorative />
        {message && <p className="nm-loading-overlay__message">{message}</p>}
        {detail && <p className="nm-loading-overlay__detail">{detail}</p>}
        {showProgress ? (
          <div className="nm-loading-overlay__progress">
            <NmProgress value={clampedProgress} showPercent variant="primary" size="sm" />
          </div>
        ) : null}
      </div>
    </div>
  );

  // Escape scrolling/transformed page containers so page loaders stay in the viewport.
  return variant === 'page' && typeof document !== 'undefined'
    ? createPortal(overlay, document.body)
    : overlay;
}
